import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  ENABLE_ANALYTICS,
  GOOGLE_ANALYTICS_ID,
  GOOGLE_TAG_MANAGER_ID,
  META_PIXEL_ID,
} from '../constants/config';
import { logger } from './logger';

// Declare gtag, GTM, and Meta Pixel for TypeScript
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set' | 'js',
      targetIdOrConfig: string | Date | Record<string, unknown>,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

let isInitialized = false;
let isGTMInitialized = false;
let isMetaPixelInitialized = false;
let isConsentGiven = false;

/**
 * Check if user has given cookie consent
 */
const checkCookieConsent = async (): Promise<boolean> => {
  try {
    const consent = await AsyncStorage.getItem('cookie_consent');
    return consent === 'true';
  } catch (error) {
    logger.error('Error checking cookie consent:', error);
    return false;
  }
};

/**
 * Load Google Analytics script for web
 */
const loadGAScript = (measurementId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (Platform.OS !== 'web') {
      resolve();
      return;
    }

    // Check if script is already loaded
    if (window.gtag && window.dataLayer) {
      resolve();
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function (...args: unknown[]) {
      window.dataLayer!.push(args);
    };

    // Load the gtag script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script1.onload = () => {
      // Load the inline script
      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${measurementId}', {
          'anonymize_ip': true,
          'cookie_flags': 'SameSite=None;Secure'
        });
      `;
      document.head.appendChild(script2);
      resolve();
    };
    script1.onerror = () => {
      reject(new Error('Failed to load Google Analytics script'));
    };
    document.head.appendChild(script1);
  });
};

/**
 * Load Google Tag Manager script for web
 */
const loadGTMScript = (containerId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (Platform.OS !== 'web') {
      resolve();
      return;
    }

    // Check if GTM is already loaded
    if (document.getElementById('gtm-script')) {
      resolve();
      return;
    }

    // Initialize dataLayer for GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // Load GTM script
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject(new Error('Failed to load Google Tag Manager script'));
    };
    document.head.insertBefore(script, document.head.firstChild);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  });
};

/**
 * Load Meta Pixel script for web
 */
const loadMetaPixelScript = (pixelId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (Platform.OS !== 'web') {
      resolve();
      return;
    }

    if (document.getElementById('meta-pixel-script')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'meta-pixel-script';
    script.innerHTML = `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');
`;
    script.onerror = () => {
      reject(new Error('Failed to load Meta Pixel script'));
    };
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.alt = '';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.insertBefore(noscript, document.body.firstChild);

    resolve();
  });
};

/**
 * Initialize Meta Pixel
 * Only initializes if analytics is enabled, Pixel ID is provided, and consent is given
 */
export const initializeMetaPixel = async (): Promise<void> => {
  if (!ENABLE_ANALYTICS) {
    return;
  }

  if (!META_PIXEL_ID || !META_PIXEL_ID.trim()) {
    logger.warn('Meta Pixel ID not configured');
    return;
  }

  isConsentGiven = await checkCookieConsent();
  if (!isConsentGiven) {
    logger.log('Cookie consent not given, skipping Meta Pixel initialization');
    return;
  }

  if (isMetaPixelInitialized) {
    return;
  }

  try {
    if (Platform.OS === 'web') {
      await loadMetaPixelScript(META_PIXEL_ID.trim());
      isMetaPixelInitialized = true;
      logger.log('Meta Pixel initialized');
    } else {
      logger.log(
        'Meta Pixel: Native platform tracking not yet implemented'
      );
      isMetaPixelInitialized = true;
    }
  } catch (error) {
    logger.error('Error initializing Meta Pixel:', error);
  }
};

/**
 * Initialize Google Analytics
 * Only initializes if analytics is enabled, GA ID is provided, and consent is given
 */
export const initializeGA = async (): Promise<void> => {
  // Check if analytics is enabled
  if (!ENABLE_ANALYTICS) {
    return;
  }

  // Check if GA ID is provided
  if (!GOOGLE_ANALYTICS_ID) {
    logger.warn('Analytics ID not configured');
    return;
  }

  // Check cookie consent
  isConsentGiven = await checkCookieConsent();
  if (!isConsentGiven) {
    logger.log('Cookie consent not given, skipping GA initialization');
    return;
  }

  // Prevent double initialization
  if (isInitialized) {
    return;
  }

  try {
    if (Platform.OS === 'web') {
      await loadGAScript(GOOGLE_ANALYTICS_ID);
      isInitialized = true;
      logger.log('Analytics initialized');
    } else {
      // For React Native, you would initialize a native analytics library here
      // For now, we'll just mark as initialized
      // You can integrate with Firebase Analytics or another solution later
      logger.log(
        'Google Analytics: Native platform tracking not yet implemented'
      );
      isInitialized = true;
    }
  } catch (error) {
    logger.error('Error initializing Google Analytics:', error);
  }
};

/**
 * Initialize Google Tag Manager
 * Only initializes if analytics is enabled, GTM ID is provided, and consent is given
 */
export const initializeGTM = async (): Promise<void> => {
  // Check if analytics is enabled
  if (!ENABLE_ANALYTICS) {
    return;
  }

  // Check if GTM ID is provided
  if (!GOOGLE_TAG_MANAGER_ID) {
    logger.warn('Tag manager ID not configured');
    return;
  }

  // Check cookie consent
  isConsentGiven = await checkCookieConsent();
  if (!isConsentGiven) {
    logger.log('Cookie consent not given, skipping GTM initialization');
    return;
  }

  // Prevent double initialization
  if (isGTMInitialized) {
    return;
  }

  try {
    if (Platform.OS === 'web') {
      await loadGTMScript(GOOGLE_TAG_MANAGER_ID);
      isGTMInitialized = true;
      logger.log('Tag manager initialized');
    } else {
      // For React Native, you would initialize a native analytics library here
      logger.log(
        'Google Tag Manager: Native platform tracking not yet implemented'
      );
      isGTMInitialized = true;
    }
  } catch (error) {
    logger.error('Error initializing Google Tag Manager:', error);
  }
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isConsentGiven || !ENABLE_ANALYTICS) {
    return;
  }

  if (Platform.OS === 'web') {
    if (isInitialized && window.gtag) {
      window.gtag('config', GOOGLE_ANALYTICS_ID!, {
        page_path: path,
        page_title: title || path,
      });
    }
    if (isMetaPixelInitialized && window.fbq) {
      window.fbq('track', 'PageView');
    }
  } else {
    if (isInitialized) {
      logger.log('Page view tracked');
    }
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
): void => {
  if (!isInitialized || !isConsentGiven || !ENABLE_ANALYTICS) {
    return;
  }

  if (Platform.OS === 'web' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  } else {
    // For React Native, implement native tracking here
    logger.log('Event tracked');
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (
  userId?: string,
  properties?: Record<string, unknown>
): void => {
  if (!isInitialized || !isConsentGiven || !ENABLE_ANALYTICS) {
    return;
  }

  if (Platform.OS === 'web' && window.gtag) {
    if (userId) {
      window.gtag('set', { user_id: userId });
    }
    if (properties) {
      window.gtag('set', properties);
    }
  } else {
    // For React Native, implement native tracking here
    logger.log('User properties updated');
  }
};

/**
 * Set user ID (for authenticated users)
 */
export const setUserId = (userId: string): void => {
  setUserProperties(userId);
};

/**
 * Clear user ID (on logout)
 */
export const clearUserId = (): void => {
  if (Platform.OS === 'web' && window.gtag) {
    window.gtag('set', { user_id: null });
  }
};

/**
 * Meta Pixel: track sign-in (custom event).
 * Call after successful login.
 */
export const trackMetaPixelLogin = (): void => {
  if (
    !isConsentGiven ||
    !ENABLE_ANALYTICS ||
    !isMetaPixelInitialized ||
    Platform.OS !== 'web' ||
    !window.fbq
  ) {
    return;
  }
  window.fbq('trackCustom', 'Login');
};

/**
 * Meta Pixel: track sign-up (standard CompleteRegistration event).
 * Call after successful registration.
 */
export const trackMetaPixelCompleteRegistration = (): void => {
  if (
    !isConsentGiven ||
    !ENABLE_ANALYTICS ||
    !isMetaPixelInitialized ||
    Platform.OS !== 'web' ||
    !window.fbq
  ) {
    return;
  }
  window.fbq('track', 'CompleteRegistration');
};

/**
 * Meta Pixel: track purchase (standard Purchase event).
 * Call after successful subscription upgrade or one-time purchase.
 * @param value - Amount in the given currency (e.g. 9.99)
 * @param currency - ISO 4217 code (default 'USD')
 * @param orderId - Optional order/transaction id
 */
export const trackMetaPixelPurchase = (
  value: number,
  currency: string = 'USD',
  orderId?: string
): void => {
  if (
    !isConsentGiven ||
    !ENABLE_ANALYTICS ||
    !isMetaPixelInitialized ||
    Platform.OS !== 'web' ||
    !window.fbq
  ) {
    return;
  }
  const params: Record<string, unknown> = { value, currency };
  if (orderId) params.order_id = orderId;
  window.fbq('track', 'Purchase', params);
};

/**
 * Re-check consent and reinitialize if needed
 * Call this after user accepts cookie consent
 */
export const recheckConsentAndInitialize = async (): Promise<void> => {
  isConsentGiven = await checkCookieConsent();
  if (isConsentGiven) {
    if (!isInitialized) {
      await initializeGA();
    }
    if (!isGTMInitialized) {
      await initializeGTM();
    }
    if (!isMetaPixelInitialized) {
      await initializeMetaPixel();
    }
  }
};

/**
 * Track analytics event via GTM dataLayer
 * This is the primary method for tracking button clicks and custom events in GTM
 * The dataLayer is a JavaScript array used by Google Tag Manager to pass event data
 */
export const trackAnalyticsEvent = (
  eventData: Record<string, unknown>
): void => {
  if (!isGTMInitialized || !isConsentGiven || !ENABLE_ANALYTICS) {
    return;
  }

  if (Platform.OS === 'web' && window.dataLayer) {
    window.dataLayer.push(eventData);
  } else {
    // For React Native, implement native tracking here
    logger.log('Event tracked');
  }
};

/**
 * Track button click event
 * This is a convenience function specifically for button tracking
 */
export const trackButtonClick = (
  buttonName: string,
  buttonLocation?: string,
  additionalData?: Record<string, unknown>
): void => {
  const eventData: Record<string, unknown> = {
    event: 'button_click',
    button_name: buttonName,
    button_location: buttonLocation || 'unknown',
    ...(additionalData || {}),
  };

  // Send to GTM via dataLayer
  trackAnalyticsEvent(eventData);

  // Also send to Meta Pixel as a custom event when initialized and consent is given
  if (
    isConsentGiven &&
    ENABLE_ANALYTICS &&
    isMetaPixelInitialized &&
    Platform.OS === 'web' &&
    window.fbq
  ) {
    window.fbq('trackCustom', 'ButtonClick', {
      button_name: buttonName,
      button_location: buttonLocation || 'unknown',
      ...(additionalData || {}),
    });
  }
};

/**
 * Tutorial/tour event action types for GTM
 */
export type TutorialAction =
  | 'modal_view'
  | 'modal_start_tour'
  | 'modal_maybe_later'
  | 'modal_close'
  | 'tour_start'
  | 'step_view'
  | 'step_next'
  | 'step_skip'
  | 'step_finish'
  | 'step_start_action';

export interface TutorialEventData {
  tutorial_action: TutorialAction;
  tutorial_step_id?: string;
  tutorial_step_index?: number;
  tutorial_step_title?: string;
}

/**
 * Track tutorial/tour events (welcome modal + tooltip steps) for GTM
 */
export const trackTutorialEvent = (
  action: TutorialAction,
  data?: Partial<Omit<TutorialEventData, 'tutorial_action'>>
): void => {
  trackAnalyticsEvent({
    event: 'tutorial_event',
    tutorial_action: action,
    ...data,
  });
};

/**
 * Common event tracking helpers
 */
export const analyticsEvents = {
  // Authentication
  login: (method: string = 'email') =>
    trackEvent('Authentication', 'Login', method),
  logout: () => trackEvent('Authentication', 'Logout'),
  register: (method: string = 'email') =>
    trackEvent('Authentication', 'Register', method),

  // Color Analysis
  colorAnalysis: (source: string = 'camera') =>
    trackEvent('Color Analysis', 'Analyze', source),
  colorAnalysisComplete: (colorsFound: number) =>
    trackEvent('Color Analysis', 'Complete', undefined, colorsFound),

  // Mood Boards
  moodBoardCreate: () => trackEvent('Mood Board', 'Create'),
  moodBoardView: (moodBoardId: string) =>
    trackEvent('Mood Board', 'View', moodBoardId),
  moodBoardLike: (moodBoardId: string) =>
    trackEvent('Mood Board', 'Like', moodBoardId),
  moodBoardShare: (moodBoardId: string) =>
    trackEvent('Mood Board', 'Share', moodBoardId),

  // Subscriptions
  subscriptionUpgrade: (plan: string) =>
    trackEvent('Subscription', 'Upgrade', plan),
  subscriptionDowngrade: (plan: string) =>
    trackEvent('Subscription', 'Downgrade', plan),
  subscriptionView: () => trackEvent('Subscription', 'View Pricing'),

  // Features
  featureUse: (featureName: string) =>
    trackEvent('Feature', 'Use', featureName),
  virtualDrape: () => trackEvent('Feature', 'Virtual Drape'),
  smartWardrobe: () => trackEvent('Feature', 'Smart Wardrobe'),
  makeupMatching: () => trackEvent('Feature', 'Makeup Matching'),

  // Errors
  error: (errorType: string, errorMessage?: string) =>
    trackEvent('Error', errorType, errorMessage),
};
